import {
    Box,
    type BoxProps,
    Flex,
    type FlexProps,
    HStack,
    type StackProps,
} from '@chakra-ui/react';
import { useEffect, useRef } from 'react';
import {
    InsetShellScrollRestoration,
    SCROLLED_SHADOW,
    SHADOW_CLIP_BELOW,
    SHELL_SCROLLER_ATTR,
} from './shell-scroll';
import {
    SHELL_COLUMN_LEFT_VAR,
    SHELL_NAVBAR_END_VAR,
    ShellTexture,
} from './shell-texture';

// ── Shell ────────────────────────────────────────────────────────────────
// shadcn sidebar-01's shape, shared by the console and enterprise apps: a
// bordered rail in the sidebar tone, and beside it one flat content column
// on the page ground — a 64px top bar with a bottom rule, then the page,
// edge to edge. No gutter, no radius, no shadow at rest (the bar lifts one
// while the panel is scrolled). The shell is pinned to
// the viewport and the PANEL scrolls: rail and top bar never move, the
// scrollbar lives beside the page (under the top bar's rule), and the
// document itself never overflows — so a wheel fling can't rubber-band the
// whole chrome. Anything that needs the scroll container (virtualizers,
// scroll-to-top) finds it through shell-scroll.ts.
// --shell-content-height names the viewport slice under the top bar — the
// panel's height — for anything that wants to fill it (e.g. centered
// fallbacks). ONE geometry at every breakpoint.
//
// The Inset* names predate the flat geometry and are kept so both apps'
// call sites read unchanged.
//
// Composition (the app supplies its own Sidebar, bar content and page):
//   <InsetShell>
//       <AppSidebar {...insetRailProps} />
//       <InsetShell.Column>
//           <InsetShell.Navbar>…chrome controls…</InsetShell.Navbar>
//           <InsetShell.Panel>…the page…</InsetShell.Panel>
//       </InsetShell.Column>
//   </InsetShell>

// Geometry (one source of truth, exposed as CSS vars on the shell root).
const SHELL_NAVBAR_HEIGHT = '64px';

/** Rail styling for a Sidebar mounted in the shell (spread into the app's
 * <Sidebar>): viewport-tall beside the panel-scrolled column — ONE model
 * at every breakpoint (no mobile overlay; small screens get the same
 * collapsible rail). The rail keeps its recipe's right border: that line
 * is what separates it from the content column. Un-clipped so
 * Sidebar.Rail's hit strip can straddle that border (the nav and brand
 * band clip their own overflow). */
export const insetRailProps = {
    height: '100dvh',
    flexShrink: 0,
    overflow: 'visible',
} as const;

/** Globals for the shell (each app spreads this into its theme config's
 * globalCss). The shell root is exactly viewport-tall, so the document has
 * nothing to scroll; these only paint the ground and stop the browser
 * from rubber-banding it anyway. */
export const insetShellGlobalCss = {
    // The overscroll rubber-band paints the ROOT background beyond the
    // document edges — the page ground, in both themes (semantic token).
    // overscroll-behavior none: a fling that runs out of panel (or rail
    // nav) must not chain into a bounce of the whole chrome.
    html: {
        bg: 'bg.page',
        overscrollBehavior: 'none',
    },
    'html, body': {
        minHeight: '100%',
    },
    // @ionq/ui's globals.css (Tailwind v4) paints body{background;color}
    // inside @layer base — and Chakra emits this globalCss into a layer
    // of the same name, so stylesheet order decides a plain `body` tie.
    // 'html body' (specificity 0-0-2 vs 0-0-1) wins deterministically in
    // dev and prod. Do NOT "simplify" to `body`, and no !important (it
    // would invert layer precedence and drag other base rules along).
    'html body': {
        bg: 'bg.page',
        color: 'fg',
    },
};

// Hoisted: the shell root re-renders with every app-layout render, and the
// var set never changes.
const SHELL_ROOT_VARS = {
    // Read by the sidebar's brand band too (its rule meets the bar's).
    '--shell-navbar-height': SHELL_NAVBAR_HEIGHT,
    // The content's visible height: viewport minus the top bar — the
    // panel's own height, for viewport-pinned pages and fallbacks.
    '--shell-content-height': `calc(100dvh - var(--shell-navbar-height))`,
};

/** Viewport-pinned: exactly one viewport tall, so the page column has a
 * definite height for the panel to scroll inside of. */
function InsetShellRoot(props: FlexProps) {
    return (
        <Flex
            direction='row'
            height='100dvh'
            bg='bg.page'
            css={SHELL_ROOT_VARS}
            {...props}
        />
    );
}

/** The content column beside the rail: top bar over the page. minHeight 0
 * so the panel can shrink to the column instead of growing with the page
 * (a flex item's automatic minimum is its content). */
function InsetShellColumn(props: FlexProps) {
    const ref = useRef<HTMLDivElement>(null);
    // Flags the column while the panel is scrolled off its top, so the bar
    // can lift a shadow over the content (attribute only, no re-render).
    useEffect(() => {
        const column = ref.current;
        const panel = column?.querySelector<HTMLElement>(
            `[${SHELL_SCROLLER_ATTR}]`,
        );
        if (!column || !panel) return;
        const sync = () =>
            column.toggleAttribute(SHELL_SCROLLED_ATTR, panel.scrollTop > 0);
        sync();
        panel.addEventListener('scroll', sync, { passive: true });
        // The column's left edge for the shared top-band texture, kept in
        // step through the rail's collapse animation (the column resizes
        // every frame). Written on the top bar only: on the shell root the
        // inherited var would re-dirty every element's style each frame.
        const bar = column.querySelector('header');
        // Also the bar's trailing controls' width (everything after its
        // Spacer), on the shell root for the rail's pinned switcher —
        // written only on change, as it inherits page-wide.
        const root = column.parentElement;
        // The bar's Spacer: its one growing child.
        const spacer = bar
            ? [...bar.children].find(
                  (child) => getComputedStyle(child).flexGrow !== '0',
              )
            : undefined;
        let end = '';
        const publish = () => {
            bar?.style.setProperty(
                SHELL_COLUMN_LEFT_VAR,
                `${column.getBoundingClientRect().left}px`,
            );
            if (!bar || !spacer) return;
            const next = `${bar.getBoundingClientRect().right - spacer.getBoundingClientRect().right}px`;
            if (next !== end) {
                end = next;
                root?.style.setProperty(SHELL_NAVBAR_END_VAR, next);
            }
        };
        publish();
        const observer = new ResizeObserver(publish);
        observer.observe(column);
        // The spacer resizes when the trailing controls do.
        if (spacer) observer.observe(spacer);
        return () => {
            panel.removeEventListener('scroll', sync);
            observer.disconnect();
        };
    }, []);
    return (
        <Flex
            ref={ref}
            direction='column'
            flex='1'
            minWidth={0}
            minHeight={0}
            {...props}
        />
    );
}

const SHELL_SCROLLED_ATTR = 'data-shell-scrolled';
// Hoisted: static css object for the bar's scrolled state.
const NAVBAR_SCROLLED_CSS = {
    [`[${SHELL_SCROLLED_ATTR}] > &`]: { boxShadow: SCROLLED_SHADOW },
    clipPath: SHADOW_CLIP_BELOW,
};

/** The top bar (sidebar-01's header): 64px, bottom rule, 16px side inset,
 * 8px between controls. Static: the panel scrolls under its rule. */
function InsetShellNavbar({ children, ...props }: StackProps) {
    return (
        <HStack
            as='header'
            height='var(--shell-navbar-height)'
            flexShrink={0}
            px='4'
            // Cells sit flush (the bell and the account menu are ruled
            // segments); a Spacer leads, pushing them to the end.
            gap='0'
            borderBottomWidth='1px'
            borderColor='border'
            bg='bg.page'
            // Above the panel (a later sibling) so the shadow falls on it.
            position='relative'
            zIndex={1}
            transition='box-shadow 0.15s ease'
            css={NAVBAR_SCROLLED_CSS}
            {...props}
        >
            {/* The shared top-band texture (shell-texture.tsx), showing
                through the bar's translucent cells. */}
            <ShellTexture offset='column' />
            {children}
        </HStack>
    );
}

// Hoisted: the scroller marker never changes.
const SHELL_PANEL_ATTRS = { [SHELL_SCROLLER_ATTR]: '' };

/** The page area under the top bar: THE scroll container — flat, on the
 * page ground, no chrome of its own. position relative so in-page absolute
 * children have a containing block (they scroll with the page; viewport-
 * pinned overlays like the pending bar use position fixed instead).
 * overscroll-behavior contain: the panel's end is the end — no chaining
 * into the document. Marked with the scroller attribute so shell-scroll's
 * helpers (restoration, virtualizers) can find it. */
function InsetShellPanel(props: BoxProps) {
    return (
        <Box
            {...SHELL_PANEL_ATTRS}
            position='relative'
            flex='1'
            minHeight={0}
            overflow='auto'
            overscrollBehavior='contain'
            bg='bg.page'
            {...props}
        />
    );
}

export const InsetShell = Object.assign(InsetShellRoot, {
    Column: InsetShellColumn,
    Navbar: InsetShellNavbar,
    Panel: InsetShellPanel,
    ScrollRestoration: InsetShellScrollRestoration,
});
